/**
 * DogFirstCell (#545/#546) — the shared dog-first identity block used by the
 * dashboard arrivals/departures cards AND the admin Bookings rows.
 *
 * Counter Test (from the 2026-07-21 owner walkthrough): "nobody knows the
 * name of the owner… most of the things have to be by pet name." So every
 * card leads with the dog's photo (or initial-letter avatar) + the dog name
 * LARGE + breed in parentheses, with the owner name SECONDARY underneath —
 * and the status strip (the dog's flags + sex glyph) directly under the
 * lead dog's name — and, Ted #28 Phase 2 (#1050), the OWNER's flags
 * (balance `$`, store credit, unsigned agreement) directly under the owner's
 * name, because Ted: money "follows underneath the client's name".
 *
 * Multi-dog bookings: the FIRST dog leads (avatar + big name + strip); any
 * additional dogs are listed compactly below, each with its own strip, so a
 * two-dog family still reads dog-first without a wall of chrome.
 *
 * #581-followup — when an `apiUrl` is provided, every dog NAME (lead AND
 * secondary) becomes a button that opens the Dog ID Card modal for that dog
 * ("if you click on the name of the dog… the pet card with all the
 * information", owner walkthrough 2026-07-21). The name keeps its exact visual
 * weight (same classes); only a cursor/hover affordance + aria-haspopup are
 * added. All surfaces that render through this shared cell (dashboard,
 * Bookings) get the card for free. Dense typeahead rows omit `apiUrl`, so
 * their names stay plain text.
 *
 * Sawyer 01 owner-links (F2) — when a `customerId` is passed, the owner's name (lead row and
 * the owner-only fallback) is an `OwnerLink` to the client profile for the
 * roles that can open it, plain text for everyone else. The owner's account
 * flags stay siblings of the name inside `.ferro-dogfirst__owner`.
 */
import { useState } from 'react';
import type { DogCard } from '../../lib/api.js';
import { DogAvatar } from './DogAvatar.js';
import { DogStatusStrip } from './DogStatusStrip.js';
import { DogFlagChips } from './DogFlagChips.js';
import { DogIdCard } from './DogIdCard.js';
import { resolveDogFlags } from './flag-skew.js';
import { OwnerLink } from '../owner-link/index.js';

interface DogFirstCellProps {
  dogs: DogCard[];
  ownerName: string;
  /**
   * Sawyer 01 owner-links (F2) — the owner's customer id. When set (and the staffer can open
   * client profiles) the owner name links to `/customers/<id>`; absent/null
   * keeps it plain text (older server, or a placeholder name).
   */
  customerId?: string | null;
  /** Optional lead-in (e.g. a booking time "8:00 AM"). */
  prefix?: string | null;
  /**
   * #546 / Ted #7 (#910) — the row's legacy money facts. Phase 2 renders
   * money from the registry's ACCOUNT flags under the owner; these are the
   * deploy-window shim inputs only (`flag-skew.ts`) and can be dropped once
   * the Phase 2 server is live.
   */
  unpaid?: boolean;
  balanceDueCents?: number;
  storeCreditCents?: number;
  /** Avatar size — 'sm' for dense typeahead rows, 'md' (default) for cards. */
  avatarSize?: 'sm' | 'md';
  /**
   * #581-followup — when set, dog names open the Dog ID Card modal (fetched
   * from this API base). Omit to keep names as plain, non-interactive text
   * (dense pickers where clicking a name would be wrong).
   */
  apiUrl?: string;
  // Sawyer 11 client-notes-agreements (F1) — an extra owner-scoped element
  // (the "New client note" chip) drawn after the owner's flags.
  ownerAddon?: React.ReactNode;
}

function DogName({
  dog,
  primary,
  onOpen,
}: {
  dog: DogCard;
  primary: boolean;
  onOpen?: (dogId: string) => void;
}): JSX.Element {
  const weightClass = primary
    ? 'ferro-dogfirst__name-primary'
    : 'ferro-dogfirst__name-secondary';
  const inner = (
    <>
      <span className="ferro-dogfirst__dogname">{dog.name}</span>
      {dog.breed ? (
        <span className="ferro-dogfirst__breed"> ({dog.breed})</span>
      ) : null}
    </>
  );
  // No opener (typeahead rows) → plain text, unchanged from #545.
  if (!onOpen) {
    return <span className={weightClass}>{inner}</span>;
  }
  // Opener present → a bare button carrying the SAME weight classes, so the
  // name's size/weight/color is byte-identical; CSS only adds a pointer +
  // hover underline as the affordance.
  return (
    <button
      type="button"
      className={`ferro-dogfirst__namebtn ${weightClass}`}
      onClick={() => onOpen(dog.dogId)}
      aria-haspopup="dialog"
      aria-label={`Open ${dog.name}'s pet card`}
      data-testid={`dog-name-button-${dog.dogId}`}
    >
      {inner}
    </button>
  );
}

export function DogFirstCell({
  dogs,
  ownerName,
  customerId,
  prefix,
  unpaid,
  balanceDueCents,
  storeCreditCents,
  avatarSize = 'md',
  apiUrl,
  ownerAddon,
}: DogFirstCellProps): JSX.Element {
  const [openDogId, setOpenDogId] = useState<string | null>(null);
  const onOpen = apiUrl ? (id: string) => setOpenDogId(id) : undefined;
  const openDog = openDogId
    ? dogs.find((d) => d.dogId === openDogId) ?? null
    : null;

  const [lead, ...rest] = dogs;
  const legacy = { unpaid, balanceDueCents, storeCreditCents };

  const modal =
    apiUrl && openDogId ? (
      <DogIdCard
        apiUrl={apiUrl}
        dogId={openDogId}
        fallbackName={openDog?.name}
        onClose={() => setOpenDogId(null)}
      />
    ) : null;

  // Defensive: a row with no dog data still needs to render the owner (never
  // a blank cell). Falls back to an owner-led line.
  if (!lead) {
    return (
      <div className="ferro-dogfirst ferro-dogfirst--owneronly">
        <div className="ferro-dogfirst__body">
          <div className="ferro-dogfirst__owner ferro-dogfirst__owner--lead">
            {prefix ? <span className="ferro-dogfirst__prefix">{prefix} · </span> : null}
            <OwnerLink name={ownerName} customerId={customerId} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ferro-dogfirst">
      <DogAvatar name={lead.name} photoUrl={lead.photoUrl} size={avatarSize} />
      <div className="ferro-dogfirst__body">
        <div className="ferro-dogfirst__name">
          {prefix ? (
            <span className="ferro-dogfirst__prefix">{prefix} · </span>
          ) : null}
          <DogName dog={lead} primary onOpen={onOpen} />
          {rest.length > 0 ? (
            <span className="ferro-dogfirst__more">
              {' '}
              +{rest.length}
            </span>
          ) : null}
        </div>
        <DogStatusStrip dog={lead} legacy={legacy} />
        <div className="ferro-dogfirst__owner">
          <OwnerLink name={ownerName} customerId={customerId} />
          {/* Ted #28 Phase 2 — the OWNER's flags under the owner's name. */}
          <span className="ferro-dogfirst__owner-flags">
            <DogFlagChips flags={resolveDogFlags(lead, legacy)} scope="account" />
            {ownerAddon}
          </span>
        </div>
        {rest.length > 0 ? (
          <ul className="ferro-dogfirst__secondary-dogs">
            {rest.map((d) => (
              <li key={d.dogId} className="ferro-dogfirst__secondary-dog">
                <DogName dog={d} primary={false} onOpen={onOpen} />
                <DogStatusStrip dog={d} />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {modal}
    </div>
  );
}
